import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export interface Crumb {
  label: string;
  /** Omit for the current page — it renders as plain text. */
  to?: string;
}

/**
 * Ancestor trail.
 *
 * Added because deep pages had no way back up: drilling into a settings
 * sub-page or a detail route left the browser Back button as the only exit,
 * and Back is wrong after a redirect.
 */
export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
        {items.map((crumb, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${crumb.label}-${i}`} className="inline-flex items-center gap-1">
              {crumb.to && !last ? (
                <Link to={crumb.to} className="rounded hover:text-foreground focus-ring">
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current={last ? 'page' : undefined} className={cn(last && 'text-foreground')}>
                  {crumb.label}
                </span>
              )}
              {last ? null : <ChevronRight className="h-3 w-3 opacity-60" aria-hidden />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
